import type { SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableToken,
  EditableSlotGroup,
  usePropertyAddressUrl,
  usePropertyField,
} from '@vibesites/runtime/react';
import { resolveAddressHref } from '../utils/addressUrl';

export interface OfficeHourEntry {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

export const DAY_ORDER: Record<string, number> = {
  MON: 0, TUE: 1, WED: 2, THU: 3, FRI: 4, SAT: 5, SUN: 6,
};

export function dayOrderIndex(day: string | null | undefined): number {
  if (!day) return 99;
  const key = day.trim().toUpperCase().slice(0, 3);
  return DAY_ORDER[key] ?? 99;
}

export function formatDisplayPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function formatTime(raw: string | null | undefined): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (/am|pm/i.test(trimmed)) return trimmed;
  const [h, m] = trimmed.split(':').map(Number);
  if (isNaN(h)) return trimmed;
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hr = h % 12 || 12;
  return m ? `${hr}:${String(m).padStart(2, '0')} ${suffix}` : `${hr} ${suffix}`;
}

function formatHoursValue(openRaw: string | null | undefined, closeRaw: string | null | undefined): string {
  const open = (openRaw || '').trim();
  const close = (closeRaw || '').trim();
  const combined = `${open} ${close}`.toLowerCase();
  if (/appointment/.test(combined)) return 'By Appointment Only';
  if (!open && !close) return 'Closed';
  if (/closed/.test(combined) && !/\d/.test(combined)) return 'Closed';
  if (!open || !close) return 'Closed';
  return `${formatTime(open)} – ${formatTime(close)}`;
}

function formatDay(day: string | null | undefined): string {
  if (!day) return '';
  const upper = day.trim().toUpperCase();
  if (upper.length <= 3) return upper;
  return upper.slice(0, 3);
}

interface PropertyContactInfoProps {
  /** Token path prefix — must match the parent component's schema prefix (e.g. "contactus"). */
  prefix?: string;
  visitLabel?: string | null;
  callLabel?: string | null;
  hoursLabel?: string | null;
  address?: string | null;
  /** Optional link URL for the address (e.g. Google Maps / place URL). */
  addressUrl?: string | null;
  /** Link target for the address URL; defaults to "_blank". */
  addressTarget?: string | null;
  phone?: string | null;
  officeHours?: OfficeHourEntry[];
  /**
   * Slot group path for the address (e.g. "contactus.address").
   * When provided together with `addressGroupDef`, wraps the address
   * content in a live `<EditableSlotGroup>` so the editor can hit-test it.
   */
  addressGroupPath?: string;
  /** Same object literal that was passed to `useSlotGroup` — required by the editor. */
  addressGroupDef?: SlotGroupDefinition;
}

export function PropertyContactInfo({
  prefix = 'propertycontactinfo',
  visitLabel,
  callLabel,
  hoursLabel,
  address,
  addressUrl,
  addressTarget,
  phone,
  officeHours = [],
  addressGroupPath,
  addressGroupDef,
}: PropertyContactInfoProps) {
  const sorted = [...officeHours].sort((a, b) => dayOrderIndex(a.day) - dayOrderIndex(b.day));

  // Slot URL (often property_google_business_url) is only used when it passes
  // Google Maps reliability checks; otherwise fall through to the runtime
  // place → address → lat/lon chain, then a constructed address search.
  const fullAddress = usePropertyField('property_full_address') as string | null;
  const propertyAddressUrl = usePropertyAddressUrl();
  const addressHref = resolveAddressHref({
    preferredUrl: addressUrl,
    propertyAddressUrl,
    fullAddress: address || fullAddress,
  });

  const hasPhone = !!phone;
  const hasHours = sorted.length > 0;

  return (
    <div className="pci-info-cards">
      {/* Address card — always rendered so the editor can always interact with it */}
      <div className="pci-card">
        <div className="pci-card-icon">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 1C6.13 1 3 4.13 3 8c0 5.25 7 11 7 11s7-5.75 7-11c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1110 5.5a2.5 2.5 0 010 5z" fill="currentColor"/></svg>
        </div>
        <div>
          <EditableToken path={`${prefix}.visit_label`} as="h2" className="pci-card-label">
            {visitLabel || 'Visit Us'}
          </EditableToken>
          {addressGroupPath && addressGroupDef ? (
            <EditableSlotGroup groupPath={addressGroupPath} definition={addressGroupDef}>
              {addressHref ? (
                <a
                  href={addressHref}
                  target={addressTarget || '_blank'}
                  rel="noopener noreferrer"
                  className="pci-card-value pci-card-link"
                >
                  {address}
                </a>
              ) : (
                <p className="pci-card-value">{address}</p>
              )}
            </EditableSlotGroup>
          ) : addressHref ? (
            <a
              href={addressHref}
              target={addressTarget || '_blank'}
              rel="noopener noreferrer"
              className="pci-card-value pci-card-link"
            >
              <EditableToken path={`${prefix}.address`} as="span">
                {address}
              </EditableToken>
            </a>
          ) : (
            <EditableToken path={`${prefix}.address`} as="p" className="pci-card-value">
              {address}
            </EditableToken>
          )}
        </div>
      </div>

      {hasPhone && (
        <div className="pci-card">
          <div className="pci-card-icon">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M4 2a2 2 0 00-2 2v1a13 13 0 0013 13h1a2 2 0 002-2v-2.5a1 1 0 00-.68-.95l-3.12-1a1 1 0 00-1.02.27l-1.38 1.38A11 11 0 015.8 7.2l1.38-1.38a1 1 0 00.27-1.02l-1-3.12A1 1 0 005.5 1H4z" fill="currentColor"/></svg>
          </div>
          <div>
            <EditableToken path={`${prefix}.call_label`} as="h2" className="pci-card-label">
              {callLabel || 'Call Us'}
            </EditableToken>
            <a
              href={`tel:${(phone || '').replace(/[^+\d]/g, '')}`}
              type="call"
              className="dniphone dniphonehref pci-card-value pci-card-link"
            >
              <EditableToken path={`${prefix}.phone`} as="span">
                {formatDisplayPhone(phone)}
              </EditableToken>
            </a>
          </div>
        </div>
      )}

      {hasHours && (
        <div className="pci-card">
          <div className="pci-card-icon">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 0a10 10 0 100 20 10 10 0 000-20zm0 18a8 8 0 110-16 8 8 0 010 16zm.5-13H9v6l5.25 3.15.75-1.23-4.5-2.67V5z" fill="currentColor"/></svg>
          </div>
          <div>
            <EditableToken path={`${prefix}.hours_label`} as="h2" className="pci-card-label">
              {hoursLabel || 'Office Hours'}
            </EditableToken>
            <div className="pci-hours-grid">
              {sorted.map((h, i) => (
                <div key={i} className="pci-hours-row">
                  <span className="pci-hours-day">{formatDay(h.day)}</span>
                  <span className="pci-hours-time">
                    {formatHoursValue(h.open_time, h.close_time)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .pci-info-cards {
          display: flex;
          flex-direction: column;
          background: #ffffff;
          border-radius: var(--vibe-radius-lg, 12px);
          border: 1px solid var(--vibe-color-border, #e5e7eb);
          overflow: hidden;
        }
        .pci-card {
          display: flex;
          align-items: flex-start;
          gap: 0.875rem;
          padding: 1.25rem 1.5rem;
          border-bottom: 1px solid var(--vibe-color-border, #e5e7eb);
        }
        .pci-card:last-child { border-bottom: none; }
        .pci-card-icon {
          flex-shrink: 0;
          width: 36px; height: 36px;
          display: flex; align-items: center; justify-content: center;
          border-radius: 8px;
          background: color-mix(in srgb, var(--vibe-color-primary) 10%, transparent);
          color: var(--vibe-color-secondary);
          margin-top: 1px;
        }
        .pci-card-label {
          font-family: var(--vibe-font-heading);
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: #000;
          margin: 0 0 0.25rem;
        }
        .pci-card-value {
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          line-height: 1.5;
          color: var(--vibe-color-text);
          margin: 0;
        }
        .pci-card-link {
          text-decoration: none;
          color: var(--vibe-color-text);
          transition: color 0.2s, text-decoration-color 0.2s;
        }
        .pci-card-link:hover {
          color: #444;
          text-decoration: underline;
        }

        .pci-hours-grid {
          display: flex;
          flex-direction: column;
          gap: 0.2rem;
          margin-top: 0.125rem;
        }
        .pci-hours-row {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.825rem;
          line-height: 1.6;
        }
        .pci-hours-day {
          color: var(--vibe-color-text);
          font-weight: 500;
          min-width: 3rem;
        }
        .pci-hours-time {
          color: var(--vibe-color-text-muted);
          text-align: right;
        }
      `}</style>
    </div>
  );
}

export default PropertyContactInfo;
